// Rescue-craft level curve (Leechcraft, Gaolcraft).
//
// Mirrors balance.ts on the server: every 10 successful rescues earns one
// level, capped at level 10 (so 100 successful actions = max). Past max, the
// total count still climbs and we just keep flooring at 10.
//
// The server's `leechcraftLevel` / `gaolcraftLevel` DB columns hold the RAW
// COUNT (the field name is a hangover from the old triangular system). All
// derived display values come from that count via `craftProgress`.

const ACTIONS_PER_LEVEL = 10;
const LEVEL_CAP = 10;

export interface CraftProgress {
  /** Current level (0..10). Cap holds at 10 forever. */
  level: number;
  /** True at level 10 — the cost is at floor and the bar is full. */
  maxed: boolean;
  /** Total successful actions banked. */
  totalCount: number;
  /** Actions into the CURRENT level (0..9). 0 at max. */
  actionsIntoLevel: number;
  /** Actions a level spans (always 10 below max, 0 at max). */
  actionsForThisLevel: number;
  /** Actions still needed to reach the next level (0 at max). */
  actionsToNext: number;
  /** Progress through the current level, 0..100. 100 at max. */
  pct: number;
}

/** Derive craft progress from a raw successful-action count. */
export function craftProgress(totalCount: number): CraftProgress {
  const count = Math.max(0, Math.floor(totalCount || 0));
  const level = Math.min(LEVEL_CAP, Math.floor(count / ACTIONS_PER_LEVEL));
  const maxed = level >= LEVEL_CAP;
  if (maxed) {
    return {
      level: LEVEL_CAP,
      maxed: true,
      totalCount: count,
      actionsIntoLevel: ACTIONS_PER_LEVEL,
      actionsForThisLevel: 0,
      actionsToNext: 0,
      pct: 100,
    };
  }
  const actionsIntoLevel = count - level * ACTIONS_PER_LEVEL;
  return {
    level,
    maxed: false,
    totalCount: count,
    actionsIntoLevel,
    actionsForThisLevel: ACTIONS_PER_LEVEL,
    actionsToNext: ACTIONS_PER_LEVEL - actionsIntoLevel,
    pct: Math.round((actionsIntoLevel / ACTIONS_PER_LEVEL) * 100),
  };
}

/** Shared CSS for the craft box. Append once per page that renders a box. */
export const CRAFT_BOX_CSS = `
  .craft-box {
    background: var(--c-parchment);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    padding: var(--s-3);
  }
  .craft-head { display: flex; justify-content: space-between; align-items: baseline; gap: var(--s-2); }
  .craft-name { font-family: var(--font-display); font-weight: 600; color: var(--c-ink); }
  .craft-lvl {
    font-family: var(--font-numeric);
    color: var(--c-oxblood);
    font-weight: bold;
    white-space: nowrap;
  }
  .craft-sub { font-style: italic; color: var(--c-ink-soft); font-size: 0.85rem; margin: 2px 0 8px 0; }
  .craft-bar {
    height: 10px;
    background: var(--c-rule-soft);
    border-radius: 999px;
    overflow: hidden;
  }
  .craft-bar-fill { height: 100%; background: var(--c-gold-text, #b8893a); transition: width 0.4s ease; }
  .craft-xp { font-family: var(--font-numeric); font-size: 0.78rem; color: var(--c-ink-soft); margin-top: 4px; }
`;

/**
 * Render a labelled craft box: name + "Level X / 10", a one-line subtitle, a
 * progress bar, and an "X / 10 actions · N to level L+1" caption (or
 * "Mastered" once capped). `totalCount` is the raw successful-action count.
 */
export function craftBoxHtml(name: string, subtitle: string, totalCount: number): string {
  const p = craftProgress(totalCount);
  const levelLabel = p.maxed
    ? `Level ${LEVEL_CAP} · Mastered`
    : `Level ${p.level} / ${LEVEL_CAP}`;
  const caption = p.maxed
    ? `${p.totalCount} total · mastery reached`
    : `${p.actionsIntoLevel} / ${ACTIONS_PER_LEVEL} actions &middot; ${p.actionsToNext} more to level ${p.level + 1} &middot; ${p.totalCount} total`;
  return `
    <div class="craft-box">
      <div class="craft-head">
        <span class="craft-name">${name}</span>
        <span class="craft-lvl">${levelLabel}</span>
      </div>
      <div class="craft-sub">${subtitle}</div>
      <div class="craft-bar"><div class="craft-bar-fill" style="width:${p.pct}%"></div></div>
      <div class="craft-xp">${caption}</div>
    </div>
  `;
}
